import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '~/hooks';
import { IOptionValue, TFilterOptionCallback, TValueChangeCallback } from '../interfaces';

interface IUseAutocomplete<T> {
  options: IOptionValue<T>[];
  maxSuggestions?: number;
  value?: T;
  filterOptions?: TFilterOptionCallback<T>;
  onChange: TValueChangeCallback<T>;
}

export const useAutocomplete = <T>({
  options,
  maxSuggestions,
  value,
  filterOptions,
  onChange,
}: IUseAutocomplete<T>) => {
  const [data, setData] = useState<IOptionValue<T>[]>(options);

  const [isSearching, setSearching] = useState(false);
  const [isShowOptions, setShowOptions] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const isInputDirty = useRef(false);
  const optionsRef = useRef<HTMLDivElement>(null);

  const handleCloseOptions = () => {
    setShowOptions(false);
    isInputDirty.current = false;
    if (inputRef.current) {
      inputRef.current.value = value
        ? (options.find((it) => it.value === value)?.label as any)
        : '';
    }
  };
  const handleOpenOptions = () => setShowOptions(true);

  const onSearchWithDebounce = useDebounce(() => {
    setData(() => {
      const newData = options.filter((it) => {
        if (filterOptions) {
          return filterOptions(it, inputRef.current?.value || '');
        }

        return it.label?.toLowerCase()?.includes(inputRef.current?.value?.toLowerCase() || '');
      });

      if (maxSuggestions !== undefined && maxSuggestions > 0) {
        return newData.slice(0, maxSuggestions);
      }

      return newData;
    });
    setSearching(false);
  });

  const onValueChanges = (value: IOptionValue<T>) => {
    isInputDirty.current = false;
    onChange(value);
    setShowOptions(false);
  };

  useEffect(() => {
    const inputEl = inputRef.current;

    const callback = () => {
      if (!isSearching) {
        isInputDirty.current = true;
        setSearching(true);
      }

      onSearchWithDebounce();
    };

    inputEl?.addEventListener('input', callback);

    return () => {
      inputEl?.removeEventListener('input', callback);
    };
  }, []);

  useEffect(() => {
    if (value && inputRef.current) {
      const selectedOption = options.find((it) => it.value === value);

      if (selectedOption) {
        inputRef.current.value = selectedOption.label;
      }
    }
  }, [value]);

  return {
    data: isInputDirty.current ? data : options,
    handleCloseOptions,
    handleOpenOptions,
    isSearching,
    isShowOptions,
    onValueChanges,
    refs: {
      input: inputRef,
      options: optionsRef,
    },
  };
};
